import React, { useState } from 'react';
import { useAdminAuth } from '../context/AdminAuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, Lock, Key, AlertTriangle } from 'lucide-react';

export const AdminLoginPage = () => {
  const { login } = useAdminAuth();
  const [step, setStep] = useState('credentials'); // credentials, 2fa
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      
      if (result.requires2FA) {
        setStep('2fa');
      }
      // If successful without 2FA, context will handle redirect
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password, useRecovery ? null : totpCode, useRecovery ? recoveryCode : null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-gray-800 border-gray-700">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl text-white">KEIKKA Admin</CardTitle>
          <CardDescription className="text-gray-400">
            Secure administrative access
          </CardDescription>
        </CardHeader>

        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4 bg-red-900/50 border-red-800">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {step === 'credentials' && (
            <form onSubmit={handleCredentialsSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-300">Email</Label>
                <div className="relative">
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="bg-gray-700 border-gray-600 text-white pl-10"
                    placeholder="admin@keikka.work"
                    data-testid="admin-login-email"
                  />
                  <Lock className="absolute left-3 top-2.5 h-5 w-5 text-gray-500" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-300">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="bg-gray-700 border-gray-600 text-white pl-10"
                    placeholder="Enter password"
                    data-testid="admin-login-password"
                  />
                  <Key className="absolute left-3 top-2.5 h-5 w-5 text-gray-500" />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={loading}
                data-testid="admin-login-submit"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          )}

          {step === '2fa' && (
            <form onSubmit={handle2FASubmit} className="space-y-4">
              <div className="text-center mb-4">
                <p className="text-gray-400 text-sm">
                  Enter the 6-digit code from your authenticator app
                </p>
              </div>

              {!useRecovery ? (
                <div className="space-y-2">
                  <Label htmlFor="totp" className="text-gray-300">Authentication Code</Label>
                  <Input
                    id="totp"
                    type="text"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required
                    maxLength={6}
                    className="bg-gray-700 border-gray-600 text-white text-center text-2xl tracking-widest"
                    placeholder="000000"
                    autoComplete="one-time-code"
                    data-testid="admin-login-totp"
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="recovery" className="text-gray-300">Recovery Code</Label>
                  <Input
                    id="recovery"
                    type="text"
                    value={recoveryCode}
                    onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                    required
                    className="bg-gray-700 border-gray-600 text-white text-center tracking-wide"
                    placeholder="XXXX-XXXX-XXXX"
                    data-testid="admin-login-recovery"
                  />
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={loading}
                data-testid="admin-login-2fa-submit"
              >
                {loading ? 'Verifying...' : 'Verify'}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setUseRecovery(!useRecovery);
                    setTotpCode('');
                    setRecoveryCode('');
                  }}
                  className="text-sm text-blue-400 hover:text-blue-300"
                >
                  {useRecovery ? 'Use authenticator app instead' : 'Use recovery code'}
                </button>
              </div>

              <button
                type="button"
                onClick={() => {
                  setStep('credentials');
                  setTotpCode('');
                  setRecoveryCode('');
                  setError('');
                }}
                className="w-full text-sm text-gray-500 hover:text-gray-400"
              >
                Back to login
              </button>
            </form>
          )}

          <div className="mt-6 pt-4 border-t border-gray-700">
            <p className="text-xs text-gray-500 text-center">
              This is a secure administrative area. All actions are logged.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLoginPage;
